import React, { useEffect, useState } from 'react';
import {
  ILegalTransactionRelationship,
  initLegalTransactionRelationship,
} from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  IProcessManager,
  IProcessManagerLandRegister,
} from '../../Interfaces/IProcessManager';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import { z } from 'zod';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { useGetProcessManagerTypes } from '../../hooks/reactQuerys/ProcessManagerQueries';
import { useGetFederalStateLogins } from '../../hooks/reactQuerys/FederalStateLoginQueries';
import { useGetUsers } from '../../hooks/data';
import {
  AccountBalance,
  Add,
  ArrowBack,
  Business,
  Diversity3,
  HelpOutline,
  TrackChanges,
} from '@mui/icons-material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { ILegalTransactionRelationshipMember } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { random } from 'lodash';
import { useCourts } from '../../Aemter/hooks';
import { useGetHbciAccounts } from '../../hooks/reactQuerys/HbciAccountQueries';
import { useGetAppArray } from '../../hooks/reactQuerys/AppQueries';
import { HbciAccountWizardDialog } from '../HbciAccountWizardDialog';

interface IProps {
  relationsships: ILegalTransactionRelationship[];
  setRelationships: Function;
  processManagerArray: IProcessManager[];
  setProcessManagerArray: Function;
  legalTransaction: ILegalTransactionFullObject;
}

const selectOptionsSchema = z.object({
  idProcessManagerType: z.number().default(0),
  DateToCheck: z.string().default(new Date().toISOString().split('T')[0]),
  idFederalStateLogin: z.number().nullable().default(null),
  idRealEstateLandRegisterEntry: z.number().nullable().default(null),
  idGrundbuchblatt: z.number().nullable().default(null),
  idLegalClient: z.number().nullable().default(null),
  idHbciAccount: z.number().nullable().default(null),
  TargetAmount: z.string().default(''),
});
type ISelectOptions = z.infer<typeof selectOptionsSchema>;

/* ------------------------------------------------------------------ */
/* Metadaten pro ProcessManagerType                                    */
/* Fuer alle bekannten IDs hinterlegen wir Icon + Beschreibung; fuer   */
/* spaeter ergaenzte Typen greift der generische Fallback.             */
/* ------------------------------------------------------------------ */
interface TypeMeta {
  icon: React.ReactElement;
  description: string;
  chip?: string;
}

const TYPE_META: Record<number, TypeMeta> = {
  10: {
    icon: <HomeWorkIcon />,
    description:
      'Prüft regelmäßig, ob im Grundbuch des betroffenen Vertragsgegenstands eine Änderung eingetragen wurde — z. B. neuer Eigentümer, Veränderung von Belastungen oder neue Eintragung in Abt. II/III.',
    chip: 'Grundbuch',
  },
  11: {
    icon: <HomeWorkIcon />,
    description:
      'Verfolgt Eintragungen und Veränderungen an der Markentabelle eines Grundstücks. Sinnvoll bei laufenden Bauträger- oder Auflassungsvorgängen.',
    chip: 'Grundbuch',
  },
  20: {
    icon: <Business />,
    description:
      'Beobachtet das Handelsregister der hinterlegten juristischen Person. Bei Änderungen (Geschäftsführerwechsel, Sitzverlegung, Eintragungen) erhalten Sie automatisch eine Benachrichtigung.',
    chip: 'Handelsregister',
  },
  21: {
    icon: <Diversity3 />,
    description:
      'Überwacht die Gesellschafterliste der hinterlegten Gesellschaft. Sobald eine aktualisierte Liste hinterlegt wird, wird der Vorgang informiert.',
    chip: 'Gesellschafterliste',
  },
  22: {
    icon: <AccountBalance />,
    description:
      'Ruft regelmäßig den aktuellen Saldo eines Notaranderkontos über HBCI/FinTS ab. Optional kann eine Zielsumme hinterlegt werden — sobald diese erreicht ist, wird der Mandant automatisch informiert.',
    chip: 'Notaranderkonto',
  },
};

const fallbackMeta: TypeMeta = {
  icon: <HelpOutline />,
  description:
    'Automatisierte Routine. Beschreibung folgt — bitte beim Anwendungs-Administrator nachfragen.',
};

export const AddProcessManagerInformationTrackingDialog: React.FC<IProps> = ({
  relationsships,
  setRelationships,
  processManagerArray,
  setProcessManagerArray,
  legalTransaction,
}) => {
  const { processManagerTypes, isLoadingManagerTypes } =
    useGetProcessManagerTypes();
  const { federalStateLogins, isFederalStateLogins } =
    useGetFederalStateLogins();
  const { users, isLoadingUsers } = useGetUsers();
  const { courts, isLoadingCourts } = useCourts();

  const [currentSettings, setCurrentSettings] = useState<ISelectOptions>(
    selectOptionsSchema.parse({})
  );
  const [isOpen, setIsOpen] = useState(false);
  // Interner Wizard-Schritt: 0 = Typ wählen (Karten), 1 = Details
  const [step, setStep] = useState<0 | 1>(0);

  // Blatt-zentrisch (#1108): Vertragsgegenstand der Nachverfolgung ist ein am
  // Vorgang verknüpftes Grundbuchblatt (GrundbuchblattArray), nicht mehr ein
  // RealEstate-Flurstück. Dedupliziert über idGrundbuchblatt.
  const blattObjects = Array.from(
    new Map(
      relationsships
        .flatMap(rel => rel.GrundbuchblattArray ?? [])
        .map(b => [b.idGrundbuchblatt, b])
    ).values()
  );
  const selectedBlatt =
    currentSettings.idGrundbuchblatt === null
      ? undefined
      : blattObjects.find(
          b =>
            Number(b.idGrundbuchblatt) ===
            Number(currentSettings.idGrundbuchblatt)
        );
  const selectedCourt = courts?.find(
    court => Number(court.idCourt) === Number(selectedBlatt?.idCourt)
  );
  const selectedFederalStateId =
    selectedCourt?.idFederalState != null
      ? Number(selectedCourt.idFederalState)
      : null;
  // Firmen / juristische Personen aus Relationships (Members)
  const juristicClients = Array.from(
    new Map(
      (relationsships ?? [])
        .flatMap(r => r.Members ?? [])
        .filter(
          m =>
            true ||
            (m?.LegalClient?.idBusinessForm &&
              m.LegalClient.idBusinessForm !== 1)
        )
        .map(m => [
          m.idLegalClient,
          {
            idLegalClient: m.idLegalClient,
            label:
              m.LegalClient?.LastName ??
              m.LegalClient?.SurName ??
              `LegalClient ${m.idLegalClient}`,
          },
        ])
    ).values()
  );

  const requiresLandRegister = [10, 11].includes(
    currentSettings.idProcessManagerType
  );
  const requiresCompany = [20, 21].includes(
    currentSettings.idProcessManagerType
  );
  const requiresHbciAccount = [22].includes(
    currentSettings.idProcessManagerType
  );

  // HBCI-Konten dieses Vorgangs laden (nur wenn Type 22 selektiert) +
  // Modul-Gate: Type 22 nur sichtbar machen, wenn hbci_connection aktiv.
  const { appArray: _hbciAppArray } = useGetAppArray();
  const hbciConnectionActive =
    _hbciAppArray?.find(a => a.system_key === 'hbci_connection')
      ?.IsEffective === true;
  const { hbciAccounts, refetchHbciAccounts } = useGetHbciAccounts(
    requiresHbciAccount && legalTransaction.idLegalTransaction
      ? legalTransaction.idLegalTransaction
      : null
  );
  const [hbciWizardOpen, setHbciWizardOpen] = useState(false);
  // Wenn der User ueber den Wizard ein neues Konto angelegt hat, selektieren
  // wir es im Dropdown vor, sobald der Refetch durch ist.
  const [pendingHbciAutoSelect, setPendingHbciAutoSelect] = useState<
    number | null
  >(null);
  useEffect(() => {
    if (
      pendingHbciAutoSelect &&
      hbciAccounts.some(a => a.idHbciAccount === pendingHbciAutoSelect)
    ) {
      setCurrentSettings(prev => ({
        ...prev,
        idHbciAccount: pendingHbciAutoSelect,
      }));
      setPendingHbciAutoSelect(null);
    }
  }, [hbciAccounts, pendingHbciAutoSelect]);

  const isDisabled =
    (requiresLandRegister && currentSettings.idGrundbuchblatt === null) ||
    (requiresCompany && currentSettings.idLegalClient === null) ||
    (requiresHbciAccount && currentSettings.idHbciAccount === null);

  useEffect(() => {
    if (
      !requiresLandRegister ||
      currentSettings.idFederalStateLogin === null ||
      selectedFederalStateId === null
    ) {
      return;
    }

    const selectedFederalStateLogin = federalStateLogins?.find(
      federalLogin =>
        Number(federalLogin.idFederalStateLogin) ===
        Number(currentSettings.idFederalStateLogin)
    );

    if (
      selectedFederalStateLogin &&
      Number(selectedFederalStateLogin.idFederalState) !==
        selectedFederalStateId
    ) {
      setCurrentSettings(current => ({
        ...current,
        idFederalStateLogin: null,
      }));
    }
  }, [
    currentSettings.idFederalStateLogin,
    federalStateLogins,
    requiresLandRegister,
    selectedFederalStateId,
  ]);

  const handleOpen = () => {
    addRelationshipsForJuristicLegalClients();
    setCurrentSettings(selectOptionsSchema.parse({}));
    setStep(0);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setStep(0);
  };

  const companyFormationDefaults = () => ({
    type: 'companyFormation' as const,
    ShareCapital: 0,
    isFounding: false,
    PurposeOfTheCompany: '',
    AmountShares: 0,
    ValuePerShare: 0,
    useSampleProtocol: false,
    CommentRepresentativeRegulation: null,
    CommentRealEstate: null,
    idCompanyFormationOption: null,
  });

  const addRelationshipsForJuristicLegalClients = () => {
    const juristic = (legalTransaction.LegalClientArray ?? []).filter(
      c => c.idBusinessForm && c.idBusinessForm !== 1
    );

    const existing = new Set(
      relationsships.flatMap(r => (r.Members ?? []).map(m => m.idLegalClient))
    );

    const nextTempId = () => {
      const minId = relationsships.reduce(
        (m, r) => Math.min(m, r.idRelationship),
        0
      );
      return minId <= 0 ? minId - 1 : -1;
    };

    const additions: ILegalTransactionRelationship[] = [];
    for (const client of juristic) {
      if (existing.has(client.idLegalClient)) continue;

      const relId = nextTempId();

      const member: ILegalTransactionRelationshipMember = {
        FirstName: client.FirstName ?? null,
        LastName: client.LastName ?? client.SurName ?? '',
        LegalClient: client,
        LegalTransactionArray: [],
        RelationshipRole: 'Firma',
        idLegalClient: client.idLegalClient,
        idLegalClientRelationship: -random(1, Number.MAX_SAFE_INTEGER),
        idRelationship: relId,
        idRelationshipRole: 11,
        isActiveLegalClientRelationship: true,
        Joined_At: new Date().toISOString().slice(0, 19).replace('T', ' '),
        Last_Update: new Date().toISOString().slice(0, 19).replace('T', ' '),
      };

      const base = initLegalTransactionRelationship();
      additions.push({
        ...base,
        idRelationshipType: 4,
        RelationshipType: 'Firma',
        data: companyFormationDefaults(),
        Members: [member],
      });

      existing.add(client.idLegalClient);
    }

    if (additions.length) setRelationships([...relationsships, ...additions]);
  };

  const handleAdd = () => {
    let tmpId = -1;
    if (processManagerArray.length > 0) {
      const tmpIdArray =
        Math.min(...processManagerArray.map(pm => pm.idProcessManager)) - 1;
      if (tmpIdArray <= tmpId) {
        tmpId = tmpIdArray - 1;
      }
    }

    if (requiresCompany) {
      const newProcess: IProcessManagerLandRegister = {
        Closed_at: null,
        Created_at: new Date(),
        data: {
          idLegalClient: Number(currentSettings.idLegalClient),
        } as any,
        DateToCheck: currentSettings.DateToCheck,
        ExitCodeLastTry: null,
        idLegalClient: Number(currentSettings.idLegalClient),
        idLegalTransaction: -1,
        idProcessManager: tmpId,
        idProcessManagerState: 1,
        idProcessManagerType: currentSettings.idProcessManagerType,
        idRelationship: null,
        LastTry_at: null,
        Tries: 0,
        Updated_at: new Date(),
        Automations: [],
      };
      setProcessManagerArray([...processManagerArray, newProcess]);
      handleClose();
      return;
    }

    if (requiresHbciAccount) {
      // CustomeTextField type='euro' liefert je nach Eingabe entweder string
      // oder number — also defensiv normalisieren, sonst crasht ``.trim`` auf
      // einer Zahl.
      const rawTarget = currentSettings.TargetAmount as unknown;
      const targetAmountTrimmed =
        rawTarget === null || rawTarget === undefined
          ? ''
          : String(rawTarget).trim();
      const newProcess: IProcessManagerLandRegister = {
        Closed_at: null,
        Created_at: new Date(),
        data: {
          idHbciAccount: Number(currentSettings.idHbciAccount),
          ...(targetAmountTrimmed
            ? { TargetAmount: targetAmountTrimmed.replace(',', '.') }
            : {}),
        } as any,
        DateToCheck: currentSettings.DateToCheck,
        ExitCodeLastTry: null,
        idLegalClient: null,
        idLegalTransaction: -1,
        idProcessManager: tmpId,
        idProcessManagerState: 1,
        idProcessManagerType: currentSettings.idProcessManagerType,
        idRelationship: null,
        LastTry_at: null,
        Tries: 0,
        Updated_at: new Date(),
        Automations: [],
      };
      setProcessManagerArray([...processManagerArray, newProcess]);
      handleClose();
      return;
    }

    if (requiresLandRegister) {
      const newProcess: IProcessManagerLandRegister = {
        Closed_at: null,
        Created_at: new Date(),
        data: {
          // Zugang wird nicht mehr an der Nachverfolgung hinterlegt; das
          // Bundesland wird beim Lauf aus dem Blatt (Gericht) abgeleitet.
          idFederalStateLogin: null,
          idGrundbuchblatt: Number(currentSettings.idGrundbuchblatt),
        },
        DateToCheck: currentSettings.DateToCheck,
        ExitCodeLastTry: null,
        idLegalClient: null,
        idLegalTransaction: -1,
        idProcessManager: tmpId,
        idProcessManagerState: 1,
        idProcessManagerType: currentSettings.idProcessManagerType,
        idRelationship: null,
        LastTry_at: null,
        Tries: 0,
        Updated_at: new Date(),
        Automations: [],
      };
      setProcessManagerArray([...processManagerArray, newProcess]);
    }

    handleClose();
  };

  if (!isOpen) {
    return (
      <Tooltip title='Neue Informationsverfolgung anlegen'>
        <IconButton
          sx={{ float: 'right' }}
          onClick={handleOpen}
          color='primary'
        >
          <Add />
        </IconButton>
      </Tooltip>
    );
  }

  if (
    isLoadingManagerTypes ||
    isFederalStateLogins ||
    isLoadingUsers ||
    isLoadingCourts
  ) {
    return <CircularProgress />;
  }

  if (!processManagerTypes || !federalStateLogins || !users || !courts) {
    return <Alert severity='error'>Es ist ein Fehler aufgetreten!</Alert>;
  }

  // Sichtbare Tracking-Typen — pro Bank-Modul-Gate
  const visibleTypes = processManagerTypes
    .filter(type => Boolean(type.isVisible))
    .filter(type => type.idProcessManagerType !== 22 || hbciConnectionActive);

  const selectedType = processManagerTypes.find(
    t => t.idProcessManagerType === currentSettings.idProcessManagerType
  );
  const selectedMeta =
    TYPE_META[currentSettings.idProcessManagerType] ?? fallbackMeta;

  return (
    <>
      <Dialog open maxWidth='md' fullWidth onClose={handleClose}>
        <DialogTitle>
          <Stack direction='row' spacing={1} alignItems='center'>
            <TrackChanges color='primary' />
            <Box sx={{ flex: 1 }}>
              <Typography variant='h6' component='div'>
                Neue Informationsverfolgung
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Automatisierte Routine zur Überwachung von Daten zu diesem
                Vorgang.
              </Typography>
            </Box>
          </Stack>
          <Stepper activeStep={step} sx={{ mt: 2 }}>
            <Step>
              <StepLabel>Art der Verfolgung wählen</StepLabel>
            </Step>
            <Step>
              <StepLabel>Details festlegen</StepLabel>
            </Step>
          </Stepper>
        </DialogTitle>

        <DialogContent dividers>
          {/* ------------------ Schritt 0: Typ-Karten ------------------ */}
          {step === 0 && (
            <Stack spacing={2}>
              <Typography variant='body2' color='text.secondary'>
                Wählen Sie aus, was DiNo für diesen Vorgang im Hintergrund
                überwachen soll. Sie können jederzeit weitere Verfolgungen
                ergänzen.
              </Typography>
              <Grid container spacing={2}>
                {visibleTypes.map(type => {
                  const meta =
                    TYPE_META[type.idProcessManagerType] ?? fallbackMeta;
                  const isSelected =
                    currentSettings.idProcessManagerType ===
                    type.idProcessManagerType;
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      key={`pmtype-card-${type.idProcessManagerType}`}
                    >
                      <Card
                        variant='outlined'
                        sx={{
                          height: '100%',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderWidth: isSelected ? 2 : 1,
                          transition: 'border-color 120ms ease',
                        }}
                      >
                        <CardActionArea
                          onClick={() => {
                            setCurrentSettings(prev => ({
                              ...prev,
                              idProcessManagerType: type.idProcessManagerType,
                              // bei Typ-Wechsel die typ-spezifischen Felder
                              // zuruecksetzen
                              idFederalStateLogin: null,
                              idRealEstateLandRegisterEntry: null,
                              idGrundbuchblatt: null,
                              idLegalClient: null,
                              idHbciAccount: null,
                              TargetAmount: '',
                            }));
                            setStep(1);
                          }}
                          sx={{ height: '100%', alignItems: 'stretch' }}
                        >
                          <CardContent>
                            <Stack
                              direction='row'
                              spacing={1.5}
                              alignItems='flex-start'
                            >
                              <Box
                                sx={{
                                  color: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center',
                                  pt: 0.5,
                                }}
                              >
                                {meta.icon}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Stack
                                  direction='row'
                                  spacing={1}
                                  alignItems='center'
                                  sx={{ mb: 0.5 }}
                                >
                                  <Typography variant='subtitle1'>
                                    {type.ProcessManagerType}
                                  </Typography>
                                  {meta.chip && (
                                    <Chip
                                      size='small'
                                      label={meta.chip}
                                      variant='outlined'
                                    />
                                  )}
                                </Stack>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  {meta.description}
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          )}

          {/* ------------------ Schritt 1: Details --------------------- */}
          {step === 1 && (
            <Stack spacing={2}>
              <Card variant='outlined' sx={{ bgcolor: 'action.hover' }}>
                <CardContent sx={{ py: 1.5 }}>
                  <Stack direction='row' spacing={1.5} alignItems='flex-start'>
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        pt: 0.5,
                      }}
                    >
                      {selectedMeta.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant='subtitle1'>
                        {selectedType?.ProcessManagerType ??
                          'Informationsverfolgung'}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {selectedMeta.description}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CustomeTextField
                    label='Ab Datum zu prüfen'
                    attr='DateToCheck'
                    object={currentSettings}
                    setObject={setCurrentSettings}
                    type='date'
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {requiresCompany ? (
                    <CustomeTextField
                      label='Unternehmen / juristische Person'
                      attr='idLegalClient'
                      object={currentSettings}
                      setObject={setCurrentSettings}
                      type='select'
                      null={currentSettings.idLegalClient === null}
                    >
                      {juristicClients.map(c => (
                        <MenuItem
                          key={`pm-company-${c.idLegalClient}`}
                          value={c.idLegalClient}
                        >
                          {c.label}
                        </MenuItem>
                      ))}
                    </CustomeTextField>
                  ) : requiresHbciAccount ? (
                    <Stack direction='row' spacing={1} alignItems='flex-start'>
                      <Box sx={{ flex: 1 }}>
                        <CustomeTextField
                          label='Notaranderkonto'
                          attr='idHbciAccount'
                          object={currentSettings}
                          setObject={setCurrentSettings}
                          type='select'
                          null={currentSettings.idHbciAccount === null}
                        >
                          {hbciAccounts.map(acc => (
                            <MenuItem
                              key={`pm-hbci-${acc.idHbciAccount}`}
                              value={acc.idHbciAccount}
                            >
                              {acc.IBAN}
                              {acc.BankName ? ` — ${acc.BankName}` : ''}
                            </MenuItem>
                          ))}
                        </CustomeTextField>
                      </Box>
                      <Tooltip title='Neues Notaranderkonto über Bank-Login anlegen'>
                        <IconButton
                          color='primary'
                          onClick={() => setHbciWizardOpen(true)}
                          sx={{ mt: 1 }}
                        >
                          <AccountBalance />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : requiresLandRegister ? (
                    blattObjects.length === 0 ? (
                      <Alert severity='info'>
                        Am Vorgang ist noch kein Grundbuch verknüpft. Fügen Sie
                        zuerst unter „Vertragsgegenstände / Grundbuch" ein
                        Grundbuchblatt hinzu — danach kann es hier als
                        Nachverfolgung gewählt werden.
                      </Alert>
                    ) : (
                      <CustomeTextField
                        label='Vertragsgegenstand (Grundbuchblatt)'
                        attr='idGrundbuchblatt'
                        object={currentSettings}
                        setObject={setCurrentSettings}
                        type='select'
                        null={currentSettings.idGrundbuchblatt === null}
                      >
                        {blattObjects.map(b => (
                          <MenuItem
                            key={`add-pm-blatt-${b.idGrundbuchblatt}`}
                            value={b.idGrundbuchblatt}
                          >
                            Blatt {b.Blattnummer}
                            {b.Amtsgericht ? `, AG ${b.Amtsgericht}` : ''}
                            {b.Gemarkung ? `, Gemarkung ${b.Gemarkung}` : ''}
                          </MenuItem>
                        ))}
                      </CustomeTextField>
                    )
                  ) : null}
                </Grid>
              </Grid>

              {/* Zielsumme nur bei Notaranderkonto */}
              <Collapse in={requiresHbciAccount}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <CustomeTextField
                      label='Zielsumme (optional, in EUR)'
                      attr='TargetAmount'
                      object={currentSettings}
                      setObject={setCurrentSettings}
                      type='euro'
                    />
                    <Alert severity='info' sx={{ mt: 1 }}>
                      Sobald der Kontostand erstmals die Zielsumme erreicht oder
                      überschreitet, wird der Mandant per E-Mail informiert.
                      Saldo-Änderungen lösen unabhängig davon weiterhin
                      Benachrichtigungen aus.
                    </Alert>
                  </Grid>
                </Grid>
              </Collapse>

              {/* Zugang wird nicht mehr an der Nachverfolgung hinterlegt */}
              <Collapse
                in={
                  requiresLandRegister &&
                  currentSettings.idGrundbuchblatt !== null
                }
              >
                <Alert severity='info' sx={{ mt: 1 }}>
                  Der Grundbuch-Zugang wird nicht mehr hier hinterlegt. Die
                  Nachverfolgung wird später unter „Nachverfolgungen" manuell
                  gestartet — dort wählen Sie je Bundesland den Zugang und geben
                  Ihr Passwort ein.
                </Alert>
              </Collapse>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          {step === 1 && (
            <Button onClick={() => setStep(0)} startIcon={<ArrowBack />}>
              Zurück
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button variant='outlined' onClick={handleClose}>
            Abbrechen
          </Button>
          {step === 1 && (
            <Button
              variant='contained'
              onClick={handleAdd}
              disabled={isDisabled}
            >
              Hinzufügen
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <HbciAccountWizardDialog
        open={hbciWizardOpen}
        idLegalTransaction={legalTransaction.idLegalTransaction}
        participants={legalTransaction.LegalClientArray ?? []}
        roles={legalTransaction.LegalRoleArray ?? []}
        onClose={() => setHbciWizardOpen(false)}
        onCreated={newId => {
          setHbciWizardOpen(false);
          if (newId) setPendingHbciAutoSelect(newId);
          void refetchHbciAccounts();
        }}
      />
    </>
  );
};
